import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import VoiceSelector from './VoiceSelector';
import ImageConfigOptions from './ImageConfigOptions';
import BgMusicSelector, { BgMusic } from './BgMusicSelector';
import SubtitleTemplateSelector from './SubtitleTemplateSelector';
import type { SubtitleTemplate, SubtitleStyle } from '../types';

// 定义InputBar的属性接口
interface InputBarProps {
  // 需要根据实际情况定义属性
  inputContent: string;
  setInputContent: (content: string) => void;
  quality: string;
  setQuality: (quality: string) => void;
  resolution: string;
  setResolution: (resolution: string) => void;
  imageSizes: string[];
  // 音色相关属性
  voices: { voice_id: string; name: string }[];
  selectedVoice: string;
  setSelectedVoice: (voice: string) => void;
  voiceSpeed: number;
  setVoiceSpeed: (speed: number) => void;
  // 背景音乐相关属性
  bgMusicList: BgMusic[];
  selectedBgMusic: string | null;
  bgMusicVolume: number;
  onBgMusicSelect: (musicUrl: string | null) => void;
  onVolumeChange: (volume: number) => void;
  // 字幕模板相关属性
  subtitleTemplates: SubtitleTemplate[];
  subtitleStyle: SubtitleStyle;
  onSubtitleStyleChange: (style: SubtitleStyle) => void;
  stateMachine: any;
  streamChatRequest: (content: string) => void;
  saveSceneList: () => void;
  restoreInputState: () => void;
  sseReaderRef: React.MutableRefObject<any>;
  setSceneList: (scenes: any[]) => void;
}

const InputBar: React.FC<InputBarProps> = ({
  inputContent,
  setInputContent,
  quality,
  setQuality,
  resolution,
  setResolution,
  imageSizes,
  // 音色相关属性
  voices,
  selectedVoice,
  setSelectedVoice,
  voiceSpeed,
  setVoiceSpeed,
  // 背景音乐相关属性
  bgMusicList,
  selectedBgMusic,
  bgMusicVolume,
  onBgMusicSelect,
  onVolumeChange,
  // 字幕模板相关属性
  subtitleTemplates,
  subtitleStyle,
  onSubtitleStyleChange,
  stateMachine,
  streamChatRequest,
  saveSceneList,
  restoreInputState,
  sseReaderRef,
  setSceneList
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation();

  return (
    <div
      id="input-bar-container"
      className={`w-full h-full flex flex-col px-6 pt-3 pb-3 box-border ${stateMachine.getInputBarCompatState() ? 'flex items-center justify-center w-auto h-auto px-[10px] py-[5px] cursor-pointer overflow-hidden' : ''}`}
      onClick={restoreInputState}
    >
      {/* 外层容器 - 包含背景和滚动条 */}
      <div className="relative w-full flex-1 overflow-hidden rounded-2xl">
        {/* 背景层 */}
        <div
          className={`absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md shadow-2xl transition-all duration-500 ${stateMachine.getInputBarCompatState() ? 'opacity-0' : 'opacity-100'}`}
        ></div>

        {/* 滚动内容容器 - 初始输入态 */}
        <div
          id="input-container-initial"
          className={`relative w-full h-full p-4 overflow-y-auto custom-scrollbar ${stateMachine.getInputBarCompatState() ? 'w-0 h-0 p-0 opacity-0 scale-95 translate-y-8 pointer-events-none' : 'opacity-100 scale-100 translate-y-0 pointer-events-auto'} transition-all duration-500`}
        >
          {/* 文本输入框和发送按钮容器 */}
          <div className="relative">
            {/* 标题 */}
            <h2 className="text-xl font-bold text-white mb-4">{t('inputBar.title')}</h2>

            <textarea
              ref={textareaRef}
              className={`w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-xl px-6 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-y mb-4`}
              placeholder={t('inputBar.placeholder')}
              onChange={(e) => {
                setInputContent(e.target.value);
              }}
              value={inputContent}
              style={{
                minHeight: '100px',
                maxHeight: '400px'
              }} // 允许调整大小，设置最小和最大高度
              onClick={restoreInputState} // 点击恢复状态
            />
          </div>

          {/* 配置选项 - 质量、分辨率 */}
          <ImageConfigOptions
            quality={quality}
            setQuality={setQuality}
            resolution={resolution}
            setResolution={setResolution}
            imageSizes={imageSizes}
          />

          {/* 配置选项 - 背景音乐 */}
          <BgMusicSelector
            bgMusicList={bgMusicList}
            selectedBgMusic={selectedBgMusic}
            bgMusicVolume={bgMusicVolume}
            onBgMusicSelect={onBgMusicSelect}
            onVolumeChange={onVolumeChange}
          />

          {/* 配置选项 - 音色 */}
          <VoiceSelector
            voices={voices}
            selectedVoice={selectedVoice}
            onVoiceSelect={setSelectedVoice}
            voiceSpeed={voiceSpeed}
            onSpeedChange={setVoiceSpeed}
          />

          {/* 配置选项 - 字幕模板 */}
          <SubtitleTemplateSelector
            templates={subtitleTemplates}
            subtitleStyle={subtitleStyle}
            onSubtitleStyleChange={onSubtitleStyleChange}
          />
        </div>
      </div>

      {/* 底部常驻发送按钮 - 固定在容器底部，不随滚动移动 */}
      <div className="mt-4 flex gap-3">
        <button
          className={`flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium py-3 px-6 rounded-xl hover:opacity-90 transition-opacity duration-300`}
          onClick={(e) => {
            e.stopPropagation(); // 防止触发容器的restoreInputState
            if (stateMachine.getInputBarCompatState()) {
              // 🔴 点击停止按钮时保存当前sceneList
              saveSceneList();

              // 🔴 关键修复：取消SSE请求
              if (sseReaderRef.current) {
                sseReaderRef.current.cancel('User clicked stop button.');
                sseReaderRef.current = null;
              }

              // 🔴 关键修复：先通知状态机停止生成，销毁播放器
              stateMachine.stopGeneration();
              // 🔴 再重置sceneList，确保回到初始状态
              setSceneList([]);
            } else if (inputContent.trim()) {
              // 发送请求
              streamChatRequest(inputContent.trim());
            }
          }}
        >
          {stateMachine.pageState === 'generating' ? (
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              <span>{t('inputBar.generating')}</span>
            </div>
          ) : (
            <span>{t('inputBar.startVideo')}</span>
          )}
        </button>

        {/* 新增空脚本视频按钮 */}
        <button
          className={`flex-1 bg-gray-800/95 border border-gray-700/50 text-white font-medium py-3 px-6 rounded-xl hover:bg-gray-700/70 transition-colors duration-300`}
          onClick={(e) => {
            e.stopPropagation(); // 防止触发容器的restoreInputState
            if (!stateMachine.getInputBarCompatState()) {
              // 发送空内容请求，标记为isEmptyScript
              streamChatRequest('', true);
            }
          }}
        >
          <span>{t('inputBar.startEmptyScript')}</span>
        </button>
      </div>
    </div>
  );
};

export default InputBar;
